import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableBody, Paper, Skeleton, Box, Typography,
  IconButton, CircularProgress, Button, TableContainer,
} from '@mui/material';
import { motion } from 'framer-motion';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ClientDataTableHead from './ClientDataTableHead';
import ClientDataTableRow from './ClientDataTableRow';
import ClientNotesEditor from './ClientNotesEditor';
import NotesAttentionCue from './NotesAttentionCue';
import { hasClientNotesContent } from './clientNotesUtils';
import Styles from './ClientDataTable.module.scss';

const ClientDataTable = React.memo((props) => {
  const notes = props.client?.notes;
  const isClientUser =
    props.userTypeContext?.toString() === props.Constants.UserType.Client;
  const canEditNotes = !isClientUser && typeof props.onSaveNotes === 'function';
  const isRtl = props.direction !== 'ltr';
  const notesPanelRef = useRef(null);

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [draftNotes, setDraftNotes] = useState(notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesGoalActive, setNotesGoalActive] = useState(false);

  useEffect(() => {
    if (!isEditingNotes) {
      setDraftNotes(notes || '');
    }
  }, [notes, isEditingNotes]);

  const handleStartEditNotes = () => {
    setDraftNotes(notes || '');
    setIsEditingNotes(true);
  };

  const handleCancelEditNotes = () => {
    setDraftNotes(notes || '');
    setIsEditingNotes(false);
  };

  const handleSaveNotes = async () => {
    if (!props.onSaveNotes) return;
    setSavingNotes(true);
    try {
      // Full JSON (includes hidden individual notes preserved by the editor).
      const valueToSave = hasClientNotesContent(draftNotes) ? draftNotes : '';
      const ok = await props.onSaveNotes(valueToSave);
      if (ok) {
        setIsEditingNotes(false);
      }
    } finally {
      setSavingNotes(false);
    }
  };

  // Notes presence counts BOTH normal and individual notes — isIndividual only
  // controls how a note is presented, never whether the notes card exists.
  const hasNotes = hasClientNotesContent(notes);
  const showNotesPanel = !isClientUser && (canEditNotes || hasNotes);
  const titleAlignClass = isRtl ? Styles.titleRtl : Styles.titleLtr;
  const notesDirClass = isRtl ? Styles.notesPanelRtl : Styles.notesPanelLtr;
  const notesSlideX = isRtl ? 40 : -40;

  return (
    <section className={Styles.panel} dir={props.direction}>
      <h2 className={`${Styles.title} ${titleAlignClass}`}>{props.t('ClientData')}</h2>
      {(props.fetchLoading || !props.client) ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={150}
          sx={{ borderRadius: '10px', margin: '0 8px 12px' }}
        />
      ) : (
        <>
          <div className={Styles.wrap}>
                      <TableContainer component={Paper} className={`${Styles.tableRoot} client-data-table`} elevation={0}>
              <Table size="medium" className={Styles.table}>
                <ClientDataTableHead t={props.t} isManageOnly={props.isManageOnly} />
                <TableBody>
                  <ClientDataTableRow {...props} />
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          {showNotesPanel && (
            <>
              <motion.div
                ref={notesPanelRef}
                className={`${Styles.notesPanel} ${notesDirClass}${notesGoalActive ? ` ${Styles.notesPanelGoal}` : ''}`}
                dir={props.direction}
                initial={{ opacity: 0, x: notesSlideX }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4, margin: '0px 0px -8% 0px' }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                onViewportEnter={() => setNotesGoalActive(true)}
                onViewportLeave={() => setNotesGoalActive(false)}
              >
                <span className={Styles.notesGoalLine} aria-hidden="true" />
                <StickyNote2OutlinedIcon className={Styles.notesIcon} fontSize="small" />
                <div className={Styles.notesBody}>
                  <div className={Styles.notesHeader}>
                    <Typography
                      className={`${Styles.notesTitle} ${isRtl ? Styles.notesTitleRtl : Styles.notesTitleLtr}`}
                    >
                      {props.t('Notes')}
                    </Typography>
                    {canEditNotes && !isEditingNotes && (
                      <IconButton
                        size="small"
                        onClick={handleStartEditNotes}
                        aria-label={props.t('Edit')}
                        className={Styles.notesEditBtn}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {canEditNotes && isEditingNotes && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={handleCancelEditNotes}
                          disabled={savingNotes}
                          aria-label={props.t('Cancel')}
                          className={Styles.notesCancelBtn}
                        >
                          <CloseOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleSaveNotes}
                          disabled={savingNotes}
                          aria-label={props.t('Save')}
                          className={Styles.notesSaveIconBtn}
                        >
                          {savingNotes
                            ? <CircularProgress size={16} color="inherit" className={Styles.notesSpinner} />
                            : <CheckOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    )}
                  </div>
                  {isEditingNotes ? (
                    <Box sx={{ opacity: savingNotes ? 0.7 : 1, pointerEvents: savingNotes ? 'none' : 'auto' }}>
                      <ClientNotesEditor
                        value={draftNotes}
                        onChange={setDraftNotes}
                        direction={props.direction}
                        t={props.t}
                        editable
                      />
                    </Box>
                  ) : hasNotes ? (
                    <Box sx={{ width: '100%' }}>
                      <ClientNotesEditor
                        value={notes || ''}
                        direction={props.direction}
                        t={props.t}
                        editable={false}
                        maskUntilShown
                      />
                    </Box>
                  ) : (
                    <Typography
                      onClick={canEditNotes ? handleStartEditNotes : undefined}
                      className={`${Styles.notesPlaceholder} ${isRtl ? Styles.notesPlaceholderRtl : Styles.notesPlaceholderLtr}`}
                      sx={{ cursor: canEditNotes ? 'pointer' : 'default' }}
                    >
                      {canEditNotes ? props.t('Notes') : ''}
                    </Typography>
                  )}
                  {canEditNotes && isEditingNotes && (
                    <div
                      className={`${Styles.notesActions} ${isRtl ? Styles.notesActionsRtl : Styles.notesActionsLtr}`}
                    >
                      <Button
                        size="small"
                        onClick={handleCancelEditNotes}
                        disabled={savingNotes}
                        className={Styles.notesBtnCancel}
                      >
                        {props.t('Cancel')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className={Styles.notesBtnSave}
                      >
                        {savingNotes ? <CircularProgress size={16} color="inherit" className={Styles.notesSpinner} /> : props.t('Save')}
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
              <NotesAttentionCue
                targetRef={notesPanelRef}
                label={props.t('Notes')}
                direction={props.direction}
              />
            </>
          )}
        </>
      )}
    </section>
  );
});

ClientDataTable.propTypes = {
  fetchLoading: PropTypes.bool,
  client: PropTypes.object,
  direction: PropTypes.string,
  t: PropTypes.func.isRequired,
  headerStyle: PropTypes.object,
  editIcon: PropTypes.string.isRequired,
  deleteIcon: PropTypes.string.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDeleteClick: PropTypes.func.isRequired,
  permissionsState: PropTypes.object.isRequired,
  userTypeContext: PropTypes.any,
  Constants: PropTypes.object.isRequired,
  subAccountants: PropTypes.array.isRequired,
  tokenDaysRemaining: PropTypes.number,
  TaxAuthorityService: PropTypes.object.isRequired,
  token: PropTypes.string,
  handleTaxAuthorityAuth: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  refreshTokenValidity: PropTypes.func,
  isManageOnly: PropTypes.bool,
  onSaveNotes: PropTypes.func,
};

export default ClientDataTable;
